import React, { useEffect, useState } from "react";
import { PageHeader, StatCard } from "../components/PageHeader";
import { useAuth } from "../lib/auth";
import api, { formatINR } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import SalesPerformancePanel from "../components/SalesPerformancePanel";
import SalesHistoryTable from "../components/SalesHistoryTable";
import AdminPayslipGenerator from "../components/AdminPayslipGenerator";
import SalesLeaderboard from "../components/SalesLeaderboard";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [finance, setFinance] = useState(null);
  const [myAttendance, setMyAttendance] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        if (isAdmin) {
          const [{ data: att }, { data: emps }, { data: fin }] = await Promise.all([
            api.get("/attendance/today-summary"),
            api.get("/employees"),
            api.get("/finance/summary"),
          ]);
          setAttendanceSummary(att); setEmployees(emps); setFinance(fin);
        } else {
          const { data } = await api.get("/attendance");
          setMyAttendance(data);
        }
      } catch (err) { /* dashboard load failure surfaces via empty state */ }
    })();
  }, [isAdmin]);

  return (
    <div>
      <PageHeader
        eyebrow="Overview"
        title={isAdmin ? "Command Centre" : `Welcome, ${user?.name?.split(" ")[0] || ""}`}
        subtitle={isAdmin ? "Real-time snapshot of workforce, attendance and finance." : "Your attendance snapshot and quick actions."}
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-8">
        {isAdmin ? (
          <>
            {/* Stat grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total Employees" value={employees.length} testId="stat-employees" />
              <StatCard label="Present Today" value={attendanceSummary?.counts?.Present ?? 0}
                        hint={attendanceSummary ? `Absent: ${attendanceSummary.counts.Absent || 0} · Leave: ${attendanceSummary.counts.Leave || 0}` : ""}
                        testId="stat-present" />
              <StatCard label="Total Income" value={formatINR(finance?.total_income || 0)} testId="stat-income" />
              <StatCard label="Net Balance" value={formatINR(finance?.net_balance || 0)}
                        hint={`Expenses ${formatINR(finance?.total_expense || 0)}`} testId="stat-net-balance" />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="lg:col-span-2 bg-white border border-[#E5E7EB] p-6">
                <div className="flex items-baseline justify-between mb-4">
                  <div>
                    <div className="label-caps">Monthly cashflow</div>
                    <div className="font-display font-bold text-xl mt-1">Income vs Expense (₹)</div>
                  </div>
                </div>
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={finance?.monthly || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                      <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                      <YAxis stroke="#6B7280" fontSize={12} tickFormatter={(v) => `₹${v / 1000}k`} />
                      <Tooltip formatter={(v) => formatINR(v)} />
                      <Legend />
                      <Bar dataKey="income" fill="#106109" />
                      <Bar dataKey="expense" fill="#DC2626" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white border border-[#E5E7EB] p-6">
                <div className="label-caps">Attendance today</div>
                <div className="font-display font-bold text-xl mt-1 mb-4">Status breakdown</div>
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={Object.entries(attendanceSummary?.counts || {}).map(([name, value]) => ({ name, value }))}
                        dataKey="value" nameKey="name" outerRadius={80} label
                      >
                        {["#106109", "#DC2626", "#FBBF24", "#10B981"].map((c) => <Cell key={c} fill={c} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-xs text-[#6B7280] text-center">
                  Unmarked: {attendanceSummary?.unmarked ?? 0}
                </div>
              </div>
            </div>

            <AdminPayslipGenerator />
            <SalesLeaderboard />
          </>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard label="Your Employee ID" value={user?.employee_id} testId="stat-emp-id" />
              <StatCard label="Department" value={user?.department} testId="stat-dept" />
              <StatCard label="Total Attendance Marked" value={myAttendance.length} testId="stat-my-att" />
            </div>
            {String(user?.department || "").toLowerCase() === "sales" && (
              <>
                <SalesPerformancePanel />
                <SalesHistoryTable />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
